import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { CashRequestStatus } from "@/constants/entity-status";
import { EstadosEfectivo } from "@/constants/enums/status/entity-status-ids";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import { CashRequestDetail } from "@/pages/requests/cash/schema/cash-requests.schema";
import { AppColors } from "@/styles/colors";
import { formatCurrency } from "@/utils/format-currency";
import { Button, Flex, Grid, GridItem, Spinner, Text } from "@chakra-ui/react";
import { Check, CircleCheck, CircleX, Info, PenLine, X } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";

interface Props {
  request?: CashRequestDetail | null;
  isEditingAmount: boolean;
  loadingRequest: boolean;
  loadingRejectRequest: boolean;
  loadingAcceptRequest: boolean;
  loadingEditAmount: boolean;
  loadingSendInfo: boolean;
  control: Control<FieldValues>;
  setOpenInfoModal: (open: boolean) => void;
  setOpenRejectModal: (open: boolean) => void;
  handleEditAmount: () => void;
  handleAcceptRequest: (status: EstadosEfectivo) => void;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  clearErrors: UseFormClearErrors<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors?: FieldErrors<FieldValues>;
}

export const CashRequestAmountAndActions = ({
  request,
  loadingEditAmount,
  loadingRejectRequest,
  loadingAcceptRequest,
  loadingRequest,
  loadingSendInfo,
  isEditingAmount,
  control,
  errors,
  handleAcceptRequest,
  handleEditAmount,
  setOpenInfoModal,
  setOpenRejectModal,
  setIsEditingAmount,
  clearErrors,
  setValue,
}: Props) => {
  const { userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.CASH_REQUEST && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex justifyContent="space-between">
      <Grid templateColumns="repeat(2, 1fr)" gap="36px">
        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              textTransform="uppercase"
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
            >
              Monto solicitado
            </Text>
            <Flex alignItems="center" gap="10px">
              {/* Cambiar a input al hacer click */}
              {isEditingAmount &&
              request?.estado === CashRequestStatus.NEEDS_INFO ? (
                <InputCurrency
                  control={control}
                  name="request_amount"
                  defaultValue={String(request?.montoSolicitado)}
                  data-testid="cash-request-amount-input"
                  maxLength={20}
                  hasError={Boolean(errors?.request_amount)}
                />
              ) : (
                <Flex alignItems="center" gap="8px">
                  <Text
                    fontSize={{
                      base: "15px",
                      sm: "15px",
                      md: "15px",
                      lg: "18px",
                      xl: "20px",
                    }}
                    fontWeight={700}
                    cursor={
                      request?.estado === CashRequestStatus.NEEDS_INFO
                        ? "pointer"
                        : "inherit"
                    }
                  >
                    ${formatCurrency(request?.montoSolicitado)}
                  </Text>
                  {request?.estado === CashRequestStatus.NEEDS_INFO &&
                    hasWriteAccess && (
                      <PenLine
                        data-testid="cash-pen-line"
                        cursor="pointer"
                        onClick={() => setIsEditingAmount(true)}
                      />
                    )}
                </Flex>
              )}
              {loadingEditAmount && isEditingAmount && (
                <Spinner data-testid="cash-request-edit-amount-spinner" />
              )}
              {isEditingAmount && !loadingEditAmount && (
                <Check
                  cursor={errors?.request_amount ? "not-allowed" : "pointer"}
                  onClick={handleEditAmount}
                />
              )}
              {isEditingAmount && (
                <X
                  data-testid="sell-order-cancel-edit-button"
                  cursor="pointer"
                  onClick={() => {
                    setIsEditingAmount(false);
                    clearErrors("request_amount");
                    setValue(
                      "request_amount",
                      String(request?.montoSolicitado),
                    );
                  }}
                />
              )}
            </Flex>
            {errors?.request_amount && (
              <Text color={AppColors.ERROR_TEXT}>
                {errors?.request_amount?.message as string}
              </Text>
            )}
          </Flex>
        </GridItem>
        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              textTransform="uppercase"
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
            >
              Saldo
            </Text>
            <Flex alignItems="center" gap="10px">
              <Text
                fontSize={{
                  base: "15px",
                  sm: "15px",
                  md: "15px",
                  lg: "18px",
                  xl: "20px",
                }}
                fontWeight={700}
                color={
                  Number(request?.saldoActual) >= 0
                    ? AppColors.RED_ICON
                    : AppColors.GREEN_ICON
                }
              >
                ${formatCurrency(Math.abs(Number(request?.saldoActual)))}
              </Text>
              {request?.control ? (
                <CircleCheck color={AppColors.GREEN_ICON} />
              ) : (
                <CircleX
                  color={AppColors.RED_ICON}
                  data-testid="cash-request-detail-circle-x-icon"
                />
              )}
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
      {hasWriteAccess &&
        request?.estado !== CashRequestStatus.REJECTED &&
        request?.estado !== CashRequestStatus.FINISHED && (
          <Flex gap="21px">
            {request?.estado !== CashRequestStatus.AVAILABLE &&
            request?.estado !== CashRequestStatus.REJECTED &&
            request?.estado !== CashRequestStatus.FINISHED ? (
              <Button
                disabled={
                  loadingRequest ||
                  loadingRejectRequest ||
                  loadingSendInfo ||
                  loadingAcceptRequest
                }
                paddingX="12px"
                paddingY="4px"
                bgColor={AppColors.LIGHT_GREEN}
                onClick={() => handleAcceptRequest(EstadosEfectivo.Disponible)}
                data-testid="cash-request-detail-exec-button"
              >
                <Check />
                <Text
                  fontWeight={600}
                  fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
                >
                  Ejecutar
                </Text>
              </Button>
            ) : (
              <Button
                disabled={
                  loadingRequest ||
                  loadingRejectRequest ||
                  loadingSendInfo ||
                  loadingAcceptRequest ||
                  request?.estado === CashRequestStatus.REJECTED ||
                  request?.estado === CashRequestStatus.FINISHED
                }
                paddingX="12px"
                paddingY="4px"
                bgColor={AppColors.GREEN_PRIMARY}
                onClick={() => handleAcceptRequest(EstadosEfectivo.Finalizada)}
              >
                <Check />
                <Text
                  fontWeight={600}
                  fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
                >
                  Finalizar
                </Text>
              </Button>
            )}

            <Button
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.BLACK_TEXT_SECONDARY}
              disabled={
                loadingRequest ||
                loadingRejectRequest ||
                loadingSendInfo ||
                loadingAcceptRequest ||
                Number(request?.mensajes?.length) >= 5 ||
                request?.estado === CashRequestStatus.REJECTED ||
                request?.estado === CashRequestStatus.AVAILABLE ||
                request?.estado === CashRequestStatus.FINISHED
              }
              onClick={() => setOpenInfoModal(true)}
            >
              <Info />
              <Text
                fontWeight={600}
                fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
              >
                Requerir información
              </Text>
            </Button>
            <Button
              disabled={
                loadingRequest ||
                loadingRejectRequest ||
                loadingSendInfo ||
                loadingAcceptRequest ||
                request?.estado === CashRequestStatus.REJECTED ||
                request?.estado === CashRequestStatus.FINISHED
              }
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.RED_ICON}
              onClick={() => setOpenRejectModal(true)}
            >
              <X />
              <Text
                fontWeight={600}
                fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
              >
                Rechazar
              </Text>
            </Button>
          </Flex>
        )}
    </Flex>
  );
};
